import { z } from 'zod';

import { zId } from './id';

export const zSummarizedBrandSpecialPrices = z
  .object({
    price_id: z.string().optional(),
    discounted_price: z.number().optional(),
  })
  .partial();

export const zSummarizedBrandSpecial = z
  .object({
    id: z.number().optional(),
    special_type: z.string().optional(),
    title: z.string().optional(),
    discount_label: z.string().optional(),
    prices: z
      .union([zSummarizedBrandSpecialPrices, z.undefined(), z.null()])
      .optional(),
  })
  .partial();

export type SummarizedBrandSpecialPrices = z.infer<
  typeof zSummarizedBrandSpecialPrices
>;
export type SummarizedBrandSpecial = z.infer<typeof zSummarizedBrandSpecial>;

const zBrandSpecialType = z.enum(['product', 'buy_x_and_save', 'buy_x_get_y']);

const zProductBrand = z.object({
  id: z.number(),
  name: z.string(),
  logo_url: z.string().nullable(),
});

export const zBrandSpecialPhoto = z.object({
  id: zId,
  position: z.number(),
  urls: z.object({
    extraLarge: z.string(),
    medium: z.string(),
    original: z.string(),
    small: z.string(),
  }),
});

export const zBundleApplicableWeights = z
  .object({
    qualify: z.array(z.string()).optional(),
    discount: z.array(z.string()).optional(),
  })
  .partial();

export const zBrandSpecialUserSegmentIds = z
  .union([z.number(), z.array(z.number())])
  .nullable()
  .optional();

const zBrandSpecialInclusionRule = z
  .object({
    kinds: z.array(z.string()).optional(),
    lineages: z.array(z.string()).optional(),
    product_ids: z.array(z.number()).nullable().optional(),
    states: z.array(z.string()).nullable().optional(),
    store_ids: z.array(z.string()).nullable().optional(),
    user_segment_ids: zBrandSpecialUserSegmentIds.optional(),
    weights: z.array(z.string()).optional(),
  })
  .partial();

const zBrandSpecialExclusionRule = z
  .object({
    kinds: z.array(z.string()).optional(),
    lineages: z.array(z.string()).optional(),
    product_ids: z.array(z.number()).nullable().optional(),
    states: z.array(z.string()).nullable().optional(),
    store_ids: z.array(z.string()).nullable().optional(),
    user_segment_ids: zBrandSpecialUserSegmentIds.optional(),
    weights: z.array(z.string()).optional(),
  })
  .partial();

// Base structure for includes/excludes rule sections
const zBrandSpecialRuleSection = z
  .object({
    includes: z.array(zBrandSpecialInclusionRule).optional(),
    excludes: z.array(zBrandSpecialExclusionRule).optional(),
  })
  .partial();

// Settings object for bundle structure
const zBundleSettings = z
  .object({
    threshold_number_of_items_in_cart: z.number().nullable().optional(),
    max_number_of_discounted_products: z.number().nullable().optional(),
    discount_application_method: z.string().nullable().optional(),
    discount_distribution: z.string().nullable().optional(),
  })
  .partial();

export const zBrandSpecialRules = z
  .object({
    // Product type rules
    includes: z.array(zBrandSpecialInclusionRule).optional(),
    excludes: z.array(zBrandSpecialExclusionRule).optional(),

    // Bundle type rules
    settings: zBundleSettings.optional(),
    base: zBrandSpecialRuleSection.optional(),
    qualifier: z.array(zBrandSpecialRuleSection).optional(),
    discountable: z.array(zBrandSpecialRuleSection).optional(),
  })
  .partial();

export const zBrandSpecial = z.object({
  active: z.boolean(),
  applicable_weights: z.union([
    z.array(z.string()),
    zBundleApplicableWeights,
    z.null(),
  ]),
  created_at: z.string(),
  description: z.string(),
  discount_dollar_amount: z.number(),
  discount_label: z.string(),
  discount_percent: z.number(),
  discount_target_price: z.number(),
  discount_type: z.string(),
  enabled: z.boolean(),
  end_time: z.string().nullable(),
  excluded_app_modes: z.array(z.string()).nullable(),
  id: z.number(),
  photo: zBrandSpecialPhoto.nullable(),
  product_brand: zProductBrand.nullable(),
  product_brand_id: z.number(),
  rules: zBrandSpecialRules,
  special_type: zBrandSpecialType,
  start_time: z.string().nullable(),
  terms: z.string().nullable(),
  title: z.string(),
  updated_at: z.string(),
});

export type BrandSpecial = z.infer<typeof zBrandSpecial>;
export type BrandSpecialRules = z.infer<typeof zBrandSpecialRules>;
export type BrandSpecialType = z.infer<typeof zBrandSpecialType>;
